/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import GoogleMapContainer from '@Components/GoogleMapContainer';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import clsx from 'clsx';
import { formatZonedDateTime } from '@Utils/utils';
import { ArrowRight, Calendar, MapPin, Star, Ticket } from 'lucide-react';
import { useMemo } from 'react';
import { MediaGallery } from '@Components/MediaGallery';
import RichText from '@Components/RichText';
import { TicketedEvent } from '@Types/ticketedEvents';
import { formatCurrency } from '@Utils/utils';
import { ToastRestaurant } from '@Types/toast';

export interface EventPreviewProps {
  event: TicketedEvent;
  venue?: {
    name: string;
    toastRestaurant?: ToastRestaurant;
  };
}

const getPriceRange = (ticketTypes: TicketedEvent['ticketTypes']) => {
  if (!ticketTypes || ticketTypes.length === 0) {
    return null;
  }
  const prices = ticketTypes.map((type) => Number(type.price));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  return minPrice === maxPrice
    ? formatCurrency(minPrice)
    : `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`;
};

export default function EventPreview({ event, venue }: EventPreviewProps) {
  const priceRange = useMemo(
    () => getPriceRange(event.ticketTypes),
    [event.ticketTypes],
  );

  const isMembersOnly = event.visibility === 'MEMBERS_ONLY';
  const displayTimeZone =
    venue?.toastRestaurant?.general?.timeZone ?? 'America/New_York';

  return (
    <div className="dark bg-zinc-950 pb-16 text-white">
      {/* Featured Image */}
      <div className="h-72 overflow-hidden bg-zinc-800">
        {event.featuredImage ? (
          <CloudinaryImage
            publicId={event.featuredImage.cloudinaryId}
            width={1200}
            mode="fill"
            className={clsx('min-h-full min-w-full object-cover object-center')}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-zinc-800 text-zinc-400">
            No featured image
          </div>
        )}
      </div>

      <div className="bg-zinc-950 p-4 pt-12">
        {/* Event Name and Badge */}
        <div className="mb-6">
          <Heading level="h2" className="mb-2 text-white">
            {event.name}
          </Heading>
          {isMembersOnly && (
            <Badge className="bg-[#CAAB6A]">
              <Star className="mr-1 inline h-3 w-3" /> Members Only
            </Badge>
          )}
        </div>

        {/* Date & Time (displayed in venue timezone) */}
        {event.startDateTime && event.endDateTime && (
          <div className="mb-2 flex items-start gap-2">
            <div className="pt-1">
              <Calendar className="h-4 w-4 text-[#caab6a]" />
            </div>
            <div>
              <p className="text-white">
                {formatZonedDateTime(
                  event.startDateTime,
                  'MMMM d, yyyy',
                  displayTimeZone,
                )}
              </p>
              <div className="flex items-center gap-1 text-sm text-zinc-400">
                <p>
                  {formatZonedDateTime(
                    event.startDateTime,
                    'h:mm a',
                    displayTimeZone,
                  )}
                </p>
                <ArrowRight className="h-4 w-4" />
                <p>
                  {formatZonedDateTime(
                    event.endDateTime,
                    'h:mm a',
                    displayTimeZone,
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Venue */}
        {venue && (
          <div className="flex items-start gap-2">
            <div className="pt-1">
              <MapPin className="h-4 w-4 text-[#caab6a]" />
            </div>
            <div>
              <p className="text-white">{venue.name}</p>
            </div>
          </div>
        )}

        {/* Description */}
        {event.description && (
          <div className="mt-4">
            <RichText
              content={event.description}
              className="prose-invert prose-sm max-w-none text-zinc-300"
            />
          </div>
        )}

        {/* Tickets Card */}
        <div className="container mx-auto mt-8">
          {event.ticketTypes && event.ticketTypes.length > 0 ? (
            <div className="flex overflow-hidden rounded-lg bg-zinc-800">
              <div className="relative flex w-12 items-center justify-center bg-[#caab6a]">
                <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-black" />
                <div className="h-full border-1 border-dashed border-black" />
                <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-black" />
              </div>
              <div className="w-full gap-4 p-4">
                <div className="shrink grow">
                  <div>
                    <div className="text-sm text-zinc-400">Tickets</div>
                    <Heading
                      level="h2"
                      styleAs="h4"
                      className="mb-1 text-white"
                    >
                      {priceRange}
                    </Heading>
                  </div>
                </div>
                <div className="mt-3 flex shrink-0 grow-0 items-center justify-between">
                  <Button size="sm" disabled>
                    Purchase tickets
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[#caab6a]/30 bg-zinc-800 p-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#caab6a]/15">
                <Ticket className="h-6 w-6 text-[#caab6a]" />
              </div>
              <Heading level="h3" styleAs="h4" className="mb-1">
                Free Event
              </Heading>
              <p className="text-sm text-zinc-400">
                No tickets required — just show up and enjoy!
              </p>
            </div>
          )}
        </div>

        {/* Media Gallery */}
        {event.images && event.images.length > 0 && (
          <div className="container mx-auto mt-8">
            <Card className="border-zinc-800 bg-zinc-900">
              <CardHeader>
                <Heading level="h3" styleAs="h4" className="text-white">
                  Media Gallery
                </Heading>
              </CardHeader>
              <CardContent>
                <MediaGallery
                  images={event.images}
                  columns={{ base: 3, sm: 4, md: 5 }}
                  gapClassName="gap-3"
                  thumbAspect="square"
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Event Location */}
        <div className="container mx-auto mt-8">
          <Card className="border-zinc-800 bg-zinc-900">
            <CardHeader>
              <Heading level="h3" styleAs="h4" className="text-white">
                Event Location
              </Heading>
              {venue && (
                <CardDescription className="text-zinc-400">
                  {venue.name}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {venue &&
              venue.toastRestaurant &&
              venue.toastRestaurant.location?.latitude &&
              venue.toastRestaurant.location?.longitude ? (
                <GoogleMapContainer
                  lat={venue.toastRestaurant.location?.latitude}
                  lng={venue.toastRestaurant.location?.longitude}
                  zoom={14}
                  style={{ height: 200, borderRadius: '0.5rem' }}
                />
              ) : (
                <div className="flex h-[200px] items-center justify-center rounded-md bg-zinc-700 px-8 text-sm text-zinc-400 italic">
                  Location not available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cancellation Policy */}
        <div className="container mx-auto mt-8">
          <Card className="border-zinc-800 bg-zinc-900">
            <CardHeader>
              <Heading level="h3" styleAs="h4" className="text-white">
                Member Cancellation and Credit Policy
              </Heading>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="mt-1 text-sm text-zinc-400">
                    Cancellations made at least 6 days before the event will
                    receive a full refund to the original payment method.
                  </p>
                </div>
                <div>
                  <p className="mt-1 text-sm text-zinc-400">
                    Cancellations made 0 to 5 days before the event will receive
                    a credit in their Stork Club wallet for the full amount.
                    This credit will be issued within 3 to 5 business days.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
